import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { getLocalStorage, setLocalStorage } from "@/utils/LocalStorageHandler.js";

/**
 * Shared post-authentication navigation (mirrors the steps SignIn.vue performs after a
 * successful login) so flows that authenticate without the login form, such as the
 * password reset auto-login, land the user in the same place.
 *
 * The auth data (user, token, session details) must already be committed to the store.
 */
export function usePostLogin() {
  const store = useStore();
  const router = useRouter();

  const showLocationModal = ref(false);
  const locationsList = ref([]);
  const currentPersonId = ref(null);
  const stLocations = computed(() => store.getters.stLocations);

  const goToDefaultRoute = (fallback) => {
    const defaultRoute = store.getters.stSidebarRoutes?.defaultRoute;
    return router.push(defaultRoute ? `/${defaultRoute}` : fallback);
  };

  const startPostLogin = async (loginResponse) => {
    const sessionDetails = loginResponse?.output?.session_details;
    const personId = loginResponse?.output?.user?.personid;
    const responseLocationId = sessionDetails?.locationid;
    currentPersonId.value = personId;

    if (sessionDetails?.hasMultipleLocations) {
      const sessionData = getLocalStorage("sessionData") || {};
      if (sessionData.locationid) {
        delete sessionData.locationid;
        setLocalStorage("sessionData", sessionData);
        store.commit("SET_SESSION_DATA", sessionData);
      }
      await store.dispatch("fetchPersonLocations", { personid: personId });
      locationsList.value = stLocations.value || [];
      showLocationModal.value = true;
      return;
    }

    if (responseLocationId) {
      await store.dispatch("setPersonLocations", responseLocationId);
      const sessionData = {
        ...(getLocalStorage("sessionData") || {}),
        locationid: String(responseLocationId),
      };
      setLocalStorage("sessionData", sessionData);
      store.commit("SET_SESSION_DATA", sessionData);
    }

    await store.dispatch("fetchSidebarRoutes", { locationid: responseLocationId });
    if (responseLocationId) {
      await store.dispatch("fetchDashboardData", { personid: personId, locationid: responseLocationId });
      await store.dispatch("fetchPermissionData", { locationid: responseLocationId });
    }
    await goToDefaultRoute("/");
  };

  const handleLocationSelection = async (locationId) => {
    if (!locationId) {
      showLocationModal.value = false;
      return;
    }

    try {
      await store.dispatch("setPersonLocations", locationId);

      let organizationid = null;
      const locations = stLocations.value;
      if (locations) {
        const locationList = Array.isArray(locations) ? locations : [locations];
        const foundOrg = locationList.find((org) =>
          org.locations?.some((loc) => String(loc.location_id) === String(locationId))
        );
        if (foundOrg && foundOrg.organization_id) {
          organizationid = foundOrg.organization_id;
        }
      }

      const locationIdString = String(locationId);
      const existing = getLocalStorage("sessionData") || {};
      const sessionData = {
        ...existing,
        locationid: locationIdString,
        organizationid: organizationid || existing.organizationid || null,
      };
      setLocalStorage("sessionData", sessionData);
      store.commit("SET_SESSION_DATA", sessionData);

      showLocationModal.value = false;

      await store.dispatch("fetchSidebarRoutes", { locationid: locationIdString });
      if (currentPersonId.value) {
        await store.dispatch("fetchDashboardData", {
          personid: currentPersonId.value,
          locationid: locationIdString,
        });
      }
      await store.dispatch("fetchPermissionData", { locationid: locationIdString });
      await goToDefaultRoute("/sign-in");
    } catch (e) {
      console.error("Location selection failed", e);
    }
  };

  const closeLocationModal = () => {
    showLocationModal.value = false;
  };

  return {
    showLocationModal,
    locationsList,
    startPostLogin,
    handleLocationSelection,
    closeLocationModal,
  };
}
